import { useEffect } from "react";
import i18n from "i18next";
import { getLanguage } from "../localStorage";

export const useFormValidation = () => {
  useEffect(() => {
    // Inicializar i18n con el idioma guardado
    const storedLanguage = getLanguage();
    if (storedLanguage && i18n.language !== storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }

    // Función para generar el mensaje de validación traducido
    const getValidationMessage = (input) => {
      const validationMessages = {
        valueMissing: "validation.required",
        typeMismatch:
          input.type === "email" ? "validation.email" : "validation.url",
        patternMismatch: "validation.patternMismatch",
        tooShort: "validation.tooShort",
        tooLong: "validation.tooLong",
        rangeUnderflow: "validation.rangeUnderflow",
        rangeOverflow: "validation.rangeOverflow",
        stepMismatch: "validation.stepMismatch",
        onlyNumbers: "validation.onlyNumbers",
      };

      for (const [key, value] of Object.entries(validationMessages)) {
        if (input.validity[key]) {
          return i18n.t(value, {
            min: input.minLength,
            max: input.maxLength,
            current: input.value.length,
          });
        }
      }

      return ""; // Si no hay errores, devolver un string vacío
    };

    // Función para traducir mensajes de validación
    const translateValidationMessages = () => {
      const inputs = document.querySelectorAll("input, textarea, select");

      inputs.forEach((input) => {
        // Eliminar el manejador de eventos existente
        input.removeEventListener("invalid", handleInvalidEvent);
        input.removeEventListener("input", handleInputEvent);

        // Registrar nuevos manejadores de eventos
        input.addEventListener("invalid", handleInvalidEvent);
        input.addEventListener("input", handleInputEvent);
      });
    };

    const handleInvalidEvent = (e) => {
      e.preventDefault(); // Prevenir el comportamiento por defecto del mensaje de validación

      // Desactivar el manejo del evento para evitar bucles infinitos
      e.target.removeEventListener("invalid", handleInvalidEvent);

      const message = getValidationMessage(e.target);
      e.target.setCustomValidity(message);
      e.target.reportValidity(); // Mostrar el mensaje de error

      // Reactivar el manejo del evento
      e.target.addEventListener("invalid", handleInvalidEvent);
    };

    const handleInputEvent = (e) => {
      e.target.setCustomValidity("");
    };

    // Inicializar los mensajes de validación
    translateValidationMessages(); // Se ejecuta una vez en el idioma actual

    // Volver a traducir los mensajes al cambiar el idioma
    const handleLanguageChange = () => {
      translateValidationMessages();
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
      const inputs = document.querySelectorAll("input, textarea, select");
      inputs.forEach((input) => {
        input.removeEventListener("invalid", handleInvalidEvent);
        input.removeEventListener("input", handleInputEvent);
      });
    };
  }, []);
};
